import { PAReviewModule } from "@/store/modules/paReview.module";
import { assign, Machine } from "xstate";
import { toaster } from "@/utilities/toaster";
const paReviewMachine = Machine({
    id: "paReviewMachine",
    context: {
        reviewId: "",
        stepId: "",
        openedByClient: false,
    },
    initial: "idle",
    states: {
        idle: {
            always: {
                target: "modalDormant",
            },
        },
        modalDormant: {
            on: {
                GETTING_REVIEWS: { target: "modalActive", actions: "assignReviewIdAndStepId" },
                GETTING_REVIEW_FOR_DISPLAY: { target: "modalActive.gettingReviewForDisplay", actions: "assignReviewId" },
            },
        },
        modalActive: {
            initial: "gettingReview",
            on: {
                MAKE_MODAL_DORMANT: { target: "modalDormant" },
            },
            states: {
                gettingReview: {
                    tags: ["gettingReview"],
                    invoke: {
                        id: "gettingReview",
                        src: (context, event) => {
                            if (context.openedByClient) {
                                return PAReviewModule.GetReviewDetailByIdForClient({ reviewId: context.reviewId, stepId: context.stepId });
                            }
                            return PAReviewModule.GetReviewDetailById({ reviewId: context.reviewId, stepId: context.stepId });
                        },
                        onDone: { target: "loadedReview" },
                        onError: { target: "serviceUnavailable" },
                    },
                },
                gettingReviewForDisplay: {
                    tags: ["gettingReview"],
                    invoke: {
                        id: "gettingReviewForDisplay",
                        src: (context, event) => {
                            return PAReviewModule.GetReviewDetailByIdForDisplay({ reviewId: context.reviewId });
                        },
                        onDone: { target: "loadedReview" },
                        onError: { target: "serviceUnavailable" },
                    },
                },
                loadedReview: {
                    initial: "showingReview",
                    on: {
                        GETTING_REVIEWS: { target: "gettingReview", actions: "assignReviewIdAndStepId" },
                        GETTING_REVIEW_FOR_DISPLAY: { target: "gettingReviewForDisplay", actions: "assignReviewId" },
                    },
                    states: {
                        showingReview: {
                            on: {
                                PROCESS_ACTION: [
                                    { target: "promoteReview", cond: "actionIsPromote" },
                                    { target: "rejectReview", cond: "actionIsReject" },
                                    { target: "downloadReview", cond: "actionIsDownload" },
                                    { target: "deliverReview", cond: "actionIsDeliver" },
                                    { target: "deliverToAllReview", cond: "actionIsDeliverToAll" },
                                ],
                                ENTER_TRACKER: { target: "reviewTracker" },
                            },
                        },
                        promoteReview: {
                            tags: ["reviewAction"],
                            invoke: {
                                id: "promoteReview",
                                src: (context, event) => {
                                    return PAReviewModule.SubmitReview(context.reviewId);
                                },
                                onDone: { target: "#paReviewMachine.modalActive.gettingReview", actions: "submitReview" },
                                onError: { target: "actionAborted" },
                            },
                        },
                        rejectReview: {
                            tags: ["reviewAction"],
                            invoke: {
                                id: "deliverReview",
                                src: (context, event) => {
                                    return PAReviewModule.RejectReview(event.rejectReason);
                                },
                                onDone: { target: "#paReviewMachine.modalActive.gettingReview", actions: "rejectReview" },
                                onError: { target: "actionAborted" },
                            },
                        },
                        downloadReview: {
                            tags: "download",
                            invoke: {
                                id: "downloadReview",
                                src: (context, event) => {
                                    return PAReviewModule.GeneratePAReviewPDF(context.reviewId);
                                },
                                onDone: { target: "downloadingReview" },
                                onError: { target: "actionAborted" },
                            },
                        },
                        downloadingReview: {
                            tags: "download",
                            always: { target: "#paReviewMachine.modalActive.loadedReview", actions: "downloadReviewPdf" },
                        },
                        deliverReview: {
                            tags: ["reviewAction"],
                            invoke: {
                                id: "deliverReview",
                                src: (context, event) => {
                                    return PAReviewModule.DeliverReview({ reviewId: context.reviewId, deliverToAll: false });
                                },
                                onDone: { target: "#paReviewMachine.modalActive.gettingReview", actions: "deliverReview" },
                                onError: { target: "actionAborted" },
                            },
                        },
                        deliverToAllReview: {
                            tags: ["reviewAction"],
                            invoke: {
                                id: "deliverToAllReview",
                                src: (context, event) => {
                                    return PAReviewModule.DeliverReview({ reviewId: context.reviewId, deliverToAll: true });
                                },
                                onDone: { target: "#paReviewMachine.modalActive.gettingReview", actions: "deliverToAllReview" },
                                onError: { target: "actionAborted" },
                            },
                        },
                        actionAborted: {},
                        reviewTracker: {
                            tags: ["ReviewReady"],
                            on: {
                                EXIT_TRACKER: { target: "#paReviewMachine.modalActive.loadedReview" },
                            },
                        },
                    },
                },
                serviceUnavailable: { type: "final" },
            },
        },
    },
}, {
    services: {
        delayedSuccess: (_, __) => new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve({ result: true });
            }, 5000);
        }),
    },
    actions: {
        deliverReview: () => {
            toaster.ShowSuccess("The review has been delivered successfully.");
        },
        deliverToAllReview: () => {
            toaster.ShowSuccess("The review has been delivered to all successfully.");
        },
        submitReview: () => {
            toaster.ShowSuccess("The review has been submitted successfully.");
        },
        rejectReview: () => {
            toaster.ShowSuccess("The review has been rejected for corrections.");
        },
        assignReviewIdAndStepId: assign({
            reviewId: (context, event) => event.reviewId,
            stepId: (context, event) => event.stepId,
            openedByClient: (context, event) => event.openedByClient,
        }),
        assignReviewId: assign({
            reviewId: (context, event) => event.reviewId,
        }),
        downloadReviewPdf: () => {
            const blob = new Blob([PAReviewModule.pdfData], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            document.body.appendChild(link);
            link.download = `${"[" + "" + PAReviewModule.reviewDetail.employeeName + "]" + "-" + PAReviewModule.reviewDetail.reviewCycleName}`;
            link.click();
        },
    },
    guards: {
        actionIsPromote: (ctx, evt) => evt.actionName === "promote",
        actionIsDeliver: (ctx, evt) => evt.actionName === "deliver",
        actionIsDeliverToAll: (ctx, evt) => evt.actionName === "deliverToAll",
        actionIsReject: (ctx, evt) => evt.actionName === "reject",
        actionIsDownload: (ctx, evt) => evt.actionName === "download",
    },
    activities: {},
});
export default paReviewMachine;
